import React from 'react'

interface DiffTextProps {
  before: string
  after: string
  className?: string
  showInline?: boolean
}

export function DiffText({ before, after, className = '', showInline = true }: DiffTextProps) {
  // Simple diff algorithm for highlighting changes
  const getDiffParts = (before: string, after: string) => {
    if (before === after) {
      return [{ type: 'unchanged', text: after }]
    }

    const parts: Array<{ type: 'added' | 'removed' | 'unchanged', text: string }> = []

    // Simple word-based diff for MVP
    const beforeWords = before.split(/(\s+)/)
    const afterWords = after.split(/(\s+)/)

    let i = 0, j = 0
    let buffer: { type: 'added' | 'removed' | 'unchanged', text: string } | null = null

    while (i < beforeWords.length || j < afterWords.length) {
      const beforeWord = beforeWords[i] || ''
      const afterWord = afterWords[j] || ''

      if (beforeWord === afterWord) {
        // Flush any buffer
        if (buffer) {
          parts.push(buffer)
          buffer = null
        }
        parts.push({ type: 'unchanged', text: beforeWord })
        i++
        j++
      } else if (!beforeWord) {
        // Only in after (added)
        if (buffer?.type === 'added') {
          buffer.text += afterWord
        } else {
          if (buffer) parts.push(buffer)
          buffer = { type: 'added', text: afterWord }
        }
        j++
      } else if (!afterWord) {
        // Only in before (removed)
        if (buffer?.type === 'removed') {
          buffer.text += beforeWord
        } else {
          if (buffer) parts.push(buffer)
          buffer = { type: 'removed', text: beforeWord }
        }
        i++
      } else {
        // Different words - check if they appear later
        const beforeIndex = beforeWords.indexOf(afterWord, i + 1)
        const afterIndex = afterWords.indexOf(beforeWord, j + 1)

        if (beforeIndex !== -1 && beforeIndex - i < 3) {
          // Word appears soon in before - remove intermediate words
          while (i < beforeIndex) {
            const word = beforeWords[i]
            if (buffer?.type === 'removed') {
              buffer.text += word
            } else {
              if (buffer) parts.push(buffer)
              buffer = { type: 'removed', text: word }
            }
            i++
          }
        } else if (afterIndex !== -1 && afterIndex - j < 3) {
          // Word appears soon in after - add intermediate words
          while (j < afterIndex) {
            const word = afterWords[j]
            if (buffer?.type === 'added') {
              buffer.text += word
            } else {
              if (buffer) parts.push(buffer)
              buffer = { type: 'added', text: word }
            }
            j++
          }
        } else {
          // Simple replacement
          if (buffer?.type === 'removed') {
            buffer.text += beforeWord
          } else {
            if (buffer) parts.push(buffer)
            buffer = { type: 'removed', text: beforeWord }
          }
          i++

          if (buffer?.type === 'added') {
            buffer.text += afterWord
          } else {
            if (buffer) parts.push(buffer)
            buffer = { type: 'added', text: afterWord }
          }
          j++
        }
      }
    }

    // Flush final buffer
    if (buffer) {
      parts.push(buffer)
    }

    return parts
  }

  const diffParts = getDiffParts(before, after)

  if (!showInline) {
    // Side-by-side mode for longer content
    return (
      <div className={`grid grid-cols-2 gap-4 ${className}`}>
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Vorher</div>
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-sm whitespace-pre-wrap">{before}</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Nachher</div>
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="text-sm whitespace-pre-wrap">{after}</div>
          </div>
        </div>
      </div>
    )
  }

  // Inline diff mode
  return (
    <div className={`text-sm leading-relaxed ${className}`}>
      {diffParts.map((part, index) => {
        const baseClasses = 'px-1 rounded'

        switch (part.type) {
          case 'added':
            return (
              <span
                key={index}
                className={`${baseClasses} bg-green-200 text-green-900 font-medium`}
              >
                {part.text}
              </span>
            )
          case 'removed':
            return (
              <span
                key={index}
                className={`${baseClasses} bg-red-200 text-red-900 line-through`}
              >
                {part.text}
              </span>
            )
          default:
            return <span key={index}>{part.text}</span>
        }
      })}
    </div>
  )
}

// Specialized diff component for tags
export function TagsDiff({ before, after, className = '' }: { before: string[], after: string[], className?: string }) {
  const beforeTags = Array.isArray(before) ? before : []
  const afterTags = Array.isArray(after) ? after : []

  const removedTags = beforeTags.filter(tag => !afterTags.includes(tag))
  const addedTags = afterTags.filter(tag => !beforeTags.includes(tag))
  const unchangedTags = beforeTags.filter(tag => afterTags.includes(tag))

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {/* Removed tags */}
      {removedTags.map((tag, index) => (
        <span
          key={`removed-${index}`}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 line-through"
        >
          {tag}
        </span>
      ))}

      {/* Unchanged tags */}
      {unchangedTags.map((tag, index) => (
        <span
          key={`unchanged-${index}`}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
        >
          {tag}
        </span>
      ))}

      {/* Added tags */}
      {addedTags.map((tag, index) => (
        <span
          key={`added-${index}`}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
        >
          {tag}
        </span>
      ))}
    </div>
  )
}

// Component for comparing markdown content with special handling
export function MarkdownDiff({ before, after, className = '' }: DiffTextProps) {
  // For markdown, we'll show side-by-side for better readability
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs font-medium text-red-600">Vorher</div>
          <div className="p-3 bg-red-50 border border-red-200 rounded-md max-h-96 overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-sm font-sans">{before}</pre>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium text-green-600">Nachher</div>
          <div className="p-3 bg-green-50 border border-green-200 rounded-md max-h-96 overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-sm font-sans">{after}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <span>Wörter: {before.split(/\s+/).length} → {after.split(/\s+/).length}</span>
        <span>Zeichen: {before.length} → {after.length}</span>
        <span>Zeilen: {before.split('\n').length} → {after.split('\n').length}</span>
      </div>
    </div>
  )
}